import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import {
    MarkovVariable,
    MarkovVariableCreate,
    MarkovDatasetVariableExpression,
} from "server/models"
import {
    useInput,
    StateType,
} from "client/utils"
import {
    StoreType,
    MarkovVariableValidation,

    MarkovVariableAllowedCharacters,
    MarkovExpressionConvertToView,
    MarkovExpressionParseElements,
    MarkovExpressionCalc,
    SaveMarkovVariable,

    setMarkovVariableEdit,
    setMarkovVariableReloadList,
    setMarkovVariableReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"
import ExpressionEditor from "client/pages/markov/expressionEditor"

interface EditVariableProps {
    variable: MarkovVariable
}

let isMounted: boolean

const Form = ({
    variable,
}: EditVariableProps) => {
    const model = useSelector((state: StoreType) => state.markovModel.modelView)
    const datasetSelected = useSelector((state: StoreType) => state.markovDataset.datasetSelected)

    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)
    const variableListByName = useSelector((state: StoreType) => state.markovVariable.variableListByName)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(variable.name)
    const [expression, setExpression] = useState(variable.expression)
    const [order, setOrder] = useState(variable.order)

    const [validation, setValidation] = useState<MarkovVariableValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        setExpression(MarkovExpressionConvertToView(variable.expression, variableList))

        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (isMounted && modals.length) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        // replace variable names with var_IDs
        let expressionWithVars = ""
        if (expression.trim().length) {
            const parsedExp = MarkovExpressionParseElements(expression.trim())
            parsedExp.forEach((parsed) => {
                if (variableListByName[parsed]) expressionWithVars += `var_${variableListByName[parsed].id}`
                else expressionWithVars += parsed
            })

            // validate and calculate result expression
            const calcResult = MarkovExpressionCalc(`var_${variable.id}`, expressionWithVars, variableList, lang)
            if (calcResult.error) {
                setValidation({ expression: calcResult.error })
                setSaveState(IDLE)
                setApplyState(IDLE)
                return
            }
        }

        const newVariableData: MarkovVariableCreate = {
            modelId: variable.modelId || model.id,
            name,
            order,
        }
        const newExpressionData: MarkovDatasetVariableExpression = {
            datasetId: datasetSelected.id,
            variableId: null,
            expression: expressionWithVars,
        }

        SaveMarkovVariable(
            variable.id,
            newVariableData,
            newExpressionData,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setMarkovVariableEdit({
                id: res.newVariableId || variable.id,
                ...newVariableData,
            }))

            dispatch(setMarkovVariableReloadList(false))
            setTimeout(() => {
                dispatch(setMarkovVariableReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setMarkovVariableReloadEdit(false))
                setTimeout(() => {
                    dispatch(setMarkovVariableReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input
                            type="text"
                            value={name}
                            onChange={setName}
                            onKeyDown={(e) => {
                                if (!MarkovVariableAllowedCharacters(e.key)) e.preventDefault()
                            }}
                        />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.expression}</Label>
                        <ExpressionEditor
                            value={expression}
                            onChange={(val) => setExpression(val)}
                        />
                        <Feedback>{validation && validation.expression}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.order}</Label>
                        <Input
                            type="text"
                            value={order !== null ? order : ""}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10)
                                if (!Number.isNaN(parsed)) setOrder(parsed)
                                else setOrder(null)
                            }}
                        />
                        <Feedback>{validation && validation.order}</Feedback>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
